import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import MainModal from '../Modal/MainModal';
import { useDispatch, useSelector } from 'react-redux';
import { IoCheckmarkCircle, IoSparklesSharp } from 'react-icons/io5';
// exclamation icon no longer used directly
import ResumePricing from '../../Pricing/ResumePricing';
import ExamPricing from '../../Pricing/ExamPricing';
import { ButtonSubmitGreen } from '../Buttons/Buttons';
import styles from './UpgradePopup.module.css';
import { MdError } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import { setExamSubDuration, setIsExamSubbed } from '../../../redux/states';

const STORAGE_KEY = 'bubbleUpgradeLastShown_v1';
const TWELVE_HOURS = 12 * 60 * 60 * 1000;

const UpgradePopup = () => {
    const dispatch = useDispatch();
    const { resumeSubDuration, examSubDuration } = useSelector(state => state.stateData || {});
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [pricingOpen, setPricingOpen] = useState(false);
    const [type, setType] = useState('resume'); // 'resume' or 'exam'
    const timerRef = useRef(null);

    // Ensure exam subscription info is loaded when needed (exam paths)
    useEffect(() => {
        try {
            const token = typeof window !== 'undefined' ? localStorage?.getItem('token') : null;
            if (!token) return;
            if (examSubDuration) return;

            let cancelled = false;
            (async () => {
                try {
                    const resp = await axios.get('/dashboard/get-profile', {
                        headers: { 'x-access-token': token }
                    });
                    const profile = resp?.data?.profile || {};
                    const subs = profile.subscriptions || {};
                    const examSub = subs.exam || {};
                    const premiumSub = subs.premium || {};

                    let duration = examSub.duration || 'Free Plan';
                    if (premiumSub.subscribed) duration = 'Pro Plan';

                    if (!cancelled) {
                        try {
                            dispatch(setExamSubDuration(duration));
                            dispatch(setIsExamSubbed(Boolean(examSub.subscribed || premiumSub.subscribed)));
                        } catch (_) {}
                    }
                } catch (_) {
                    // silent fail; popup will default to showing based on existing state
                }
            })();

            return () => { cancelled = true; };
        } catch (_) {}
    }, [dispatch, examSubDuration, location.pathname]);

    // Automatic popup logic
    useEffect(() => {
        try {
            const isResumePage = location.pathname.includes('resume') || location.pathname.includes('job');
            const isExamPage = location.pathname.includes('mock') || location.pathname.includes('exam');
            
            let selectedType = 'resume';
            if (isExamPage) {
                selectedType = 'exam';
            } else if (!isResumePage) {
                // Randomly choose for other pages
                selectedType = Math.random() > 0.5 ? 'resume' : 'exam';
            }
            setType(selectedType);

            // Check if already subbed to the selected type
            if (selectedType === 'resume' && resumeSubDuration === 'Per Month') return;
            // For exams, only skip popup for effective Pro Plan
            if (selectedType === 'exam' && examSubDuration === 'Pro Plan') return;

            const last = Number(localStorage.getItem(STORAGE_KEY) || '0');
            const now = Date.now();
            const stale = !last || (now - last) > TWELVE_HOURS;
            if (stale) {
                // Show 2 minutes after mount if missing or expired
                timerRef.current = setTimeout(() => {
                    setOpen(true);
                    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch(_) {}
                }, 2 * 60 * 1000);
            }
        } catch (_) {}

        return () => {
            if (timerRef.current) {
                try { clearTimeout(timerRef.current); } catch(_) {}
                timerRef.current = null;
            }
        };
    }, [resumeSubDuration, examSubDuration, location.pathname]);

    if (!open) return null;

    const handleClose = () => {
        try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch(_) {}
        setOpen(false);
    };

    const handleUpgrade = () => {
        try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch(_) {}
        setPricingOpen(true);
    };

    const isExam = type === 'exam';

    return (
        <>
        <MainModal isOpen={open} onClose={handleClose} width="650px">
            <div className={styles.container}>

                <div className={styles.header}>
                    <h2 className={styles.title}>{isExam ? 'Unlock the Exam Simulator Pro' : 'Unlock the full Job Automator'}</h2>
                    <div className={styles.premRow}>
                        <div className={styles.premPill}><IoSparklesSharp /> Premium</div>
                        <div className={styles.premDuration}>{isExam ? 'Pro Plan' : 'Per Month plan'}</div>
                        <div className={styles.premDesc}>{isExam ? '— read 80% LESS and pass your exams easily' : '— everything you need to land interviews faster'}</div>
                    </div>
                </div>

                <div className={styles.bulletBox}>
                    <ul className={styles.bullets}>
                        {(isExam 
                            ? [
                                '15+ Exams Per Month with detailed AI grading',
                                'AI Study Plans tailored to your weak areas',
                                '24/7 AI Tutor Access for complex topics',
                                'Deep Performance Insights & Progress Tracking',
                                'Peer Benchmarking & Global Rankings',
                                'Upload study materials to get curriculum-aligned exams'
                            ]
                            : [
                                '900+ Job Connections matched to your resume',
                                'Let Bubble Ai apply to jobs while you sleep',
                                'Unlimited Resume and Cover Letter optimizations for the full Month',
                                'Career Guidance + 24/7 Human Career Coach support',
                                'Deep Company & Job Match Insights',
                                'Recruiter Access and Insider Connections'
                            ]
                        ).map((t, i) => (
                            <li key={i} className={styles.bulletItem}>
                                <IoCheckmarkCircle color="#3E8F93" size={18} />
                                <span className={styles.bulletText}>{t}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className={styles.ctaWrapper}>
                    <div className={styles.cta}>
                        <ButtonSubmitGreen onClick={handleUpgrade}>
                            {isExam ? 'Go Pro — Get 60% OFF' : 'View 60% OFF — Go Monthly'}
                        </ButtonSubmitGreen>
                    </div>
                </div>

                <div className={styles.exclaimRow}>
                    <div className={styles.exclaimCircle}><MdError className={styles.exclaimIcon} /></div>
                    <small className={styles.exclaimText}>Discounted only if you upgrade via this popup</small>
                </div>

            </div>
        </MainModal>
        {pricingOpen && (
            isExam 
                ? <ExamPricing closePricing={() => setPricingOpen(false)} open={pricingOpen} />
                : <ResumePricing closePricing={() => setPricingOpen(false)} open={pricingOpen} />
        )}
        </>
    );
};

export default UpgradePopup;
